import { flip, offset, shift, useFloating } from '@floating-ui/react';
import { DE, ES, FI, FR, GB, NO, SE } from 'country-flag-icons/react/3x2';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { SupportedLanguage } from '../../../utils/text/translation';
import { useLanguage } from '../../hooks/use-language';
import { useOutsideClick } from '../../hooks/use-outside.clicked';

const FLAG_BY_LANGUAGE: Record<SupportedLanguage, typeof DE> = {
  de: DE,
  en: GB,
  es: ES,
  fi: FI,
  fr: FR,
  no: NO,
  sv: SE,
};

const Flag = ({
  code,
  size = 18,
  variant = 'menu',
}: {
  code: SupportedLanguage;
  size?: number;
  variant?: 'trigger' | 'menu';
}) => {
  const FlagSvg = FLAG_BY_LANGUAGE[code];
  if (variant === 'trigger') {
    // Force an explicit width so the flag renders at its full 3:2 aspect and
    // overflows the circular wrapper. width="auto" inside the fixed-width
    // flex parent lets the SVG letterbox (meet) to the wrapper's width,
    // shrinking the flag until the circular clip reads as an ellipse.
    const flagWidth = Math.round(size * 1.5);
    return (
      <FlagWrapper size={size} variant={variant}>
        <FlagSvg height={size} style={{ display: 'block', flexShrink: 0 }} width={flagWidth} />
      </FlagWrapper>
    );
  }
  return (
    <FlagWrapper size={size} variant={variant}>
      <FlagSvg height={size} style={{ display: 'block', flexShrink: 0 }} width="auto" />
    </FlagWrapper>
  );
};

export const LanguageSwitcher = ({ textColor }: { textColor?: string }) => {
  const { languageItems, selectLanguage, selectedLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    open,
  });

  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  useOutsideClick(containerRef, (clickedOutside) => {
    if (open && clickedOutside) {
      setOpen(false);
    }
  });

  const sortedItems = [
    selectedLanguage,
    ...languageItems.filter((item) => item.value !== selectedLanguage.value),
  ];

  return (
    <Wrapper ref={mergedRef}>
      <Trigger
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={selectedLanguage.text}
        textColor={textColor}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Flag code={selectedLanguage.value} size={26} variant="trigger" />
        <Chevron open={open}>▾</Chevron>
      </Trigger>

      {open && (
        <Menu ref={refs.setFloating} role="listbox" style={floatingStyles}>
          {sortedItems.map((item, index) => {
            const isSelected = item.value === selectedLanguage.value;
            return (
              <React.Fragment key={item.value}>
                {index === 1 && <MenuDivider />}
                <MenuItem
                  aria-selected={isSelected}
                  role="option"
                  onClick={() => {
                    void selectLanguage(item.value);
                    setOpen(false);
                  }}
                >
                  <SelectionDot selected={isSelected} />
                  <Flag code={item.value} size={18} variant="menu" />
                  <ItemLabel selected={isSelected}>{item.text}</ItemLabel>
                </MenuItem>
              </React.Fragment>
            );
          })}
        </Menu>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  grid-area: language-switcher;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Trigger = styled.button<{ textColor?: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 15px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ textColor }) => textColor ?? 'inherit'};
  height: 100%;

  &:hover {
    background-color: ${({ theme }) => theme.color.gray.darkest};
  }
`;

const Chevron = styled.span<{ open: boolean }>`
  font-size: 0.75rem;
  line-height: 1;
  opacity: 0.7;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s ease;
  display: inline-block;
`;

const FlagWrapper = styled.div<{ variant: 'trigger' | 'menu'; size: number }>`
  border-radius: ${({ variant }) => (variant === 'trigger' ? '50%' : '2px')};
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ variant, size }) =>
    variant === 'trigger' &&
    `
    width: ${size}px;
    height: ${size}px;
  `}
  box-shadow: ${({ variant }) =>
    variant === 'trigger' ? '0 0 0 1px rgba(0, 0, 0, 0.12)' : 'none'};
`;

const Menu = styled.ul`
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.1),
    0 2px 4px rgba(0, 0, 0, 0.08);
  min-width: 160px;
  z-index: 10000;
`;

const MenuDivider = styled.li`
  height: 1px;
  background: #eee;
  margin: 4px 0;
  list-style: none;
`;

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 12px 7px 10px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.color.brand.light};
  }
`;

const SelectionDot = styled.span<{ selected: boolean }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ selected, theme }) => (selected ? theme.color.gray.dark : 'transparent')};
  border: 1.5px solid ${({ selected, theme }) => (selected ? theme.color.gray.dark : 'transparent')};
`;

const ItemLabel = styled.span<{ selected: boolean }>`
  font-size: 0.875rem;
  font-weight: ${({ selected }) => (selected ? '600' : '400')};
  color: ${({ theme }) => theme.color.gray.dark};
`;
